import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  MenuItem,
  Chip,
  Checkbox,
  CircularProgress,
  Tabs,
  Tab,
} from "@material-ui/core";
import {
  AttachFile,
  Delete as DeleteIcon,
  Description,
  FlashOn,
  Visibility,
  Add as AddIcon,
  Edit as EditIcon,
  Event as EventIcon,
  Link as LinkIcon,
  Lock as LockIcon,
  ArrowForward,
} from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import InputMask from "react-input-mask";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import CardFieldInput from "../CardFieldInput";
import ConfirmationModal from "../ConfirmationModal";
import TaskEditModal from "../TaskEditModal";
import AppointmentModal from "../AppointmentModal";
import { getBackendUrl } from "../../config";

// Modal de detalhe do card - abre ao clicar num card no board novo.
// Mostra um resumo do ticket (so leitura, link pra conversa) + um campo
// de formulario por CustomFieldDefinition do board, jah preenchido com o
// valor atual (se existir) via CardFieldValue.
// Cores iguais ao badge que aparece no card do board (Boards/index.js
// TEMPERATURE_STYLE) - mantem consistencia visual entre editar e ver.
const TEMPERATURE_OPTIONS = [
  { value: "quente", label: "Quente", color: "#c62828", background: "#fdecea" },
  { value: "morno", label: "Morno", color: "#e65100", background: "#fff3e0" },
  { value: "frio", label: "Frio", color: "#1565c0", background: "#e3f2fd" },
];

// Sugere valor pra um campo personalizado VAZIO usando dado que ja existe
// no cadastro do contato/empresa, em vez de deixar o usuario digitar de
// novo algo que o sistema ja sabe - pedido real 2026-08-28 ("todos os
// dados do cliente estao no sistema e mesmo assim nao foi preenchido").
// So sugere (nao sobrescreve valor ja salvo) e casa por palavra-chave no
// NOME do campo, ja que campo personalizado e' texto livre do usuario.
const suggestValueForField = (def, card) => {
  const name = (def.name || "").toLowerCase();
  const contact = card?.ticket?.contact;
  const org = contact?.contactOrganization;
  if (!contact) return "";

  if (/telefone|celular|whatsapp|contato\s*n[uú]mero/.test(name)) return contact.number || "";
  if (/cnpj|cpf|documento/.test(name)) return org?.documentNumber || "";
  if (/raz[aã]o|empresa/.test(name)) return org?.corporateName || org?.tradeName || "";
  if (/nome/.test(name) && !/empresa/.test(name)) return contact.name || "";
  return "";
};

const BoardCardModal = ({ open, onClose, card, customFieldDefinitions, phases, onSaved }) => {
  const history = useHistory();
  const [values, setValues] = useState({});
  const [summary, setSummary] = useState("");
  const [temperature, setTemperature] = useState("");
  const [phaseId, setPhaseId] = useState("");
  const [responsavelId, setResponsavelId] = useState("");
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();
  const [users, setUsers] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskAssignee, setNewTaskAssignee] = useState("");
  const [newTaskDueDate, setNewTaskDueDate] = useState("");
  const [savingTask, setSavingTask] = useState(false);
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [linkPhone, setLinkPhone] = useState("");
  const [linkingContact, setLinkingContact] = useState(false);
  // Pedido do usuario 2026-10-01: "nao esta vindo com o Historico de
  // tudo que aconteceu" quando o card muda de board (ex: Comercial ->
  // Financeiro) + "preciso que o operador escolha" (botoes de acao
  // manual, ex: Liberado -> Tecnica OU Implantacao) + "essa opcao de
  // voltar o board precisa ter em todos" (regra "manual" generica).
  const [cardHistory, setCardHistory] = useState(null);
  // Pedido do usuario 2026-10-02: "pode ficar como uma guia dentro do
  // card porem na parte superior" - trocado de secao recolhivel no fim
  // do card pra uma aba de verdade, lado a lado com o formulario.
  const [activeTab, setActiveTab] = useState("detalhes");
  const [movementRules, setMovementRules] = useState([]);
  const [applyingRuleId, setApplyingRuleId] = useState(null);
  const [editingForeignFieldId, setEditingForeignFieldId] = useState(null);
  const [editingForeignFieldValue, setEditingForeignFieldValue] = useState("");

  useEffect(() => {
    if (!card?.id) return;
    api
      .get(`/cards/${card.id}/history`)
      .then(({ data }) => setCardHistory(data))
      .catch(() => setCardHistory(null));
    if (card.boardId) {
      api
        .get("/movement-rules", { params: { boardId: card.boardId } })
        .then(({ data }) => setMovementRules(data || []))
        .catch(() => setMovementRules([]));
    }
  }, [card?.id, card?.boardId]);

  useEffect(() => {
    api
      .get("/users/")
      .then(({ data }) => setUsers(data.users || []))
      .catch(() => {});
  }, []);

  // So os campos sem fase (valem pra todas) ou da fase ATUAL do card -
  // pedido do usuario 2026-08-27 ("cada fase tem uma informacao"), antes
  // todo campo do board aparecia em todo card, em qualquer fase.
  const visibleFieldDefinitions = (customFieldDefinitions || []).filter(
    (def) => !def.phaseId || def.phaseId === card?.phaseId
  );

  useEffect(() => {
    if (!card) return;
    const initial = {};
    visibleFieldDefinitions.forEach((def) => {
      const existing = (card.fieldValues || []).find(
        (fv) => fv.customFieldDefinitionId === def.id
      );
      initial[def.id] = existing && existing.value ? existing.value : suggestValueForField(def, card);
    });
    setValues(initial);
    setSummary(card.summary || "");
    setTemperature(card.temperature || "");
    setPhaseId(card.phaseId || "");
    setResponsavelId(card.responsavelId || "");
    setActiveTab("detalhes");
    // Depende do ID, nao do objeto "card" inteiro: o pai (Boards/index.js)
    // agora deriva "card" ao vivo do array "cards" pra tarefas/anexos
    // ficarem atualizados sem F5 (bug real 2026-08-29), o que cria um
    // objeto NOVO a cada fetchBoardDetail(). Se esse efeito dependesse do
    // objeto inteiro, qualquer refetch em background (ex: marcar uma
    // tarefa) resetaria edicoes nao salvas de resumo/temperatura/fase.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id, customFieldDefinitions]);

  // Pedido do usuario 2026-10-02: "os dados que estao vindo ja devem ser
  // preenchidos no formulario atual, pra nao ter que refazer o processo"
  // - campo do board ATUAL que ainda esta vazio e tem um campo de MESMO
  // NOME vindo de um board anterior (aba Historico, ex: "Empresa",
  // "CNPJ", "Telefone" repetidos entre Comercial e Financeiro) usa esse
  // valor como ponto de partida, em vez do operador ter que digitar tudo
  // de novo. So' preenche campo vazio - nunca sobrescreve algo que o
  // usuario ja tenha digitado ou que ja estivesse salvo (guard
  // "next[def.id]" abaixo).
  useEffect(() => {
    if (!cardHistory?.foreignFieldValues?.length) return;
    setValues((prev) => {
      let changed = false;
      const next = { ...prev };
      visibleFieldDefinitions.forEach((def) => {
        if (next[def.id]) return;
        const match = cardHistory.foreignFieldValues.find(
          (fv) =>
            fv.value &&
            (fv.customFieldDefinition?.name || "").trim().toLowerCase() ===
              (def.name || "").trim().toLowerCase()
        );
        if (match) {
          next[def.id] = match.value;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardHistory]);

  if (!card) return null;

  const handleChange = (defId, value) => {
    setValues((prev) => ({ ...prev, [defId]: value }));
  };

  const handleGenerateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const { data } = await api.post(`/cards/${card.id}/generate-summary`);
      if (data.summary) {
        setSummary(data.summary);
      } else {
        toast.info("Não foi possível gerar - confira se a IA está configurada (Roteador de IA) e se o card tem campos preenchidos.");
      }
    } catch (err) {
      toastError(err);
    } finally {
      setGeneratingSummary(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    // Mesmo motivo do Boards/index.js: sem typeArch/fileId o arquivo cai
    // na raiz da empresa em vez de company{id}/cardAttachments/{cardId}/.
    formData.append("typeArch", "cardAttachments");
    formData.append("fileId", String(card.id));
    Array.from(files).forEach((file) => formData.append("file", file));

    setUploading(true);
    try {
      await api.post(`/cards/${card.id}/attachments`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Anexo enviado!");
      onSaved();
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await api.delete(`/card-attachments/${attachmentId}`);
      toast.success("Anexo excluído!");
      onSaved();
    } catch (err) {
      toastError(err);
    }
  };

  const handleApplyMovementRule = async (rule) => {
    setApplyingRuleId(rule.id);
    try {
      await api.post(`/cards/${card.id}/apply-movement-rule/${rule.id}`);
      toast.success(`Card movido: ${rule.name || "ação aplicada"}`);
      onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setApplyingRuleId(null);
    }
  };

  const handleSaveForeignFieldValue = async (fieldValueId) => {
    try {
      await api.put(`/cards/${card.id}/history/field-values/${fieldValueId}`, {
        value: editingForeignFieldValue,
      });
      toast.success("Campo atualizado!");
      setEditingForeignFieldId(null);
      const { data } = await api.get(`/cards/${card.id}/history`);
      setCardHistory(data);
    } catch (err) {
      toastError(err);
    }
  };

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) return;
    setSavingTask(true);
    try {
      await api.post(`/cards/${card.id}/tasks`, {
        title: newTaskTitle.trim(),
        assignedUserId: newTaskAssignee || null,
        dueDate: newTaskDueDate || null,
      });
      setNewTaskTitle("");
      setNewTaskAssignee("");
      setNewTaskDueDate("");
      onSaved();
    } catch (err) {
      toastError(err);
    } finally {
      setSavingTask(false);
    }
  };

  const handleToggleTask = async (task) => {
    try {
      await api.put(`/card-tasks/${task.id}`, { done: !task.done });
      onSaved();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await api.delete(`/card-tasks/${taskId}`);
      onSaved();
    } catch (err) {
      toastError(err);
    }
  };

  const handleSave = async () => {
    try {
      await Promise.all([
        ...visibleFieldDefinitions.map((def) =>
          api.post("/card-field-values", {
            cardId: card.id,
            customFieldDefinitionId: def.id,
            value: values[def.id] || "",
          })
        ),
        api.put(`/cards/${card.id}`, {
          summary,
          temperature: temperature || null,
          responsavelId: responsavelId || null,
        }),
        // Mudar de fase aqui passa pelo MESMO endpoint que o drag-and-drop
        // usa (MoveCardService - reordena posicao, grava CardPhaseLog,
        // dispara automacao) em vez de so trocar o phaseId cru. Existe
        // pq o usuario nao conseguia mover um card ("nem arrastando nem
        // atraves de algum botao") - agora tem um jeito confiavel sem
        // depender do drag funcionar, 2026-08-28.
        ...(phaseId && phaseId !== card.phaseId
          ? [api.put(`/cards/${card.id}/move`, { targetPhaseId: phaseId, targetPosition: 0 })]
          : []),
      ]);
      toast.success("Card atualizado!");
      onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenTicket = () => {
    if (card.ticket?.uuid) {
      history.push(`/tickets/${card.ticket.uuid}`);
    }
  };

  // Pedido do usuario 2026-09-29 (print do popup "Agendar mensagem" com
  // o campo Contato vazio): leads adicionados pela busca "Buscar
  // empresas" as vezes nao tem telefone valido de WhatsApp - o card fica
  // sem contactId, e sem contato nao da' pra "Agendar mensagem" nem
  // "Agendar compromisso" pre-selecionar ninguem. Mesma validacao real
  // via WhatsApp que ja existia no fluxo de prospeccao
  // (CompanySearchPanel.tryCreateContact) - so' que agora dando pra
  // corrigir direto no card, sem precisar re-fazer a busca.
  const handleLinkContact = async () => {
    const digits = linkPhone.replace(/\D/g, "");
    if (!digits) return;
    setLinkingContact(true);
    try {
      const { data: contact } = await api.post("/contacts", {
        name: card.title || card.ticket?.contact?.name || "Contato",
        number: `55${digits}`,
      });
      await api.put(`/cards/${card.id}`, { contactId: contact.id });
      toast.success("Contato vinculado!");
      setLinkPhone("");
      onSaved();
    } catch (err) {
      const reason = err?.response?.data?.error || "";
      if (reason.includes("whatsapp")) {
        toast.error("Esse número não tem WhatsApp válido - confira e tente outro.");
      } else {
        toastError(err);
      }
    } finally {
      setLinkingContact(false);
    }
  };

  const handleDeleteCard = async () => {
    try {
      // So tira o card do board - o ticket (se tiver) nao e' afetado,
      // ver DeleteCardService.ts. Pedido do usuario 2026-08-28 ("como
      // posso apagar um card?") - nao existia nenhum jeito na UI.
      await api.delete(`/cards/${card.id}`);
      toast.success("Card excluído!");
      onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{card.ticket?.contact?.name || card.title || "Card"}</DialogTitle>
      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
      >
        <Tab label="Detalhes" value="detalhes" />
        <Tab
          label={
            cardHistory?.foreignFieldValues?.length
              ? `Histórico (${cardHistory.foreignFieldValues.length})`
              : "Histórico"
          }
          value="historico"
        />
      </Tabs>
      <DialogContent dividers>
        {activeTab === "historico" ? (
          <Box>
            {!cardHistory ? (
              <Typography variant="body2" color="textSecondary">
                Carregando...
              </Typography>
            ) : (
              <>
                <Typography variant="caption" color="textSecondary" style={{ fontWeight: "bold" }}>
                  Passagem por quadros/fases
                </Typography>
                <Box mb={2}>
                  {(cardHistory.phaseLogs || []).map((log) => (
                    <Box key={log.id} display="flex" alignItems="center" gap={1} py={0.5}>
                      <Typography variant="body2">
                        <strong>{log.phase?.board?.name}</strong> · {log.phase?.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {moment(log.enteredAt).format("DD/MM/YYYY HH:mm")}
                        {log.leftAt ? ` → ${moment(log.leftAt).format("DD/MM/YYYY HH:mm")}` : " (atual)"}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {(cardHistory.foreignFieldValues || []).length > 0 && (
                  <>
                    <Typography variant="caption" color="textSecondary" style={{ fontWeight: "bold" }}>
                      Campos preenchidos em outros quadros
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
                      Os campos com o mesmo nome no board atual já vêm preenchidos automaticamente na aba
                      "Detalhes" - não precisa digitar de novo.
                    </Typography>
                    <Box>
                      {cardHistory.foreignFieldValues.map((fv) => (
                        <Box key={fv.id} display="flex" alignItems="center" gap={1} py={0.5} flexWrap="wrap">
                          {!cardHistory.allowEditForeignData && (
                            <LockIcon style={{ fontSize: 14, color: "#888" }} />
                          )}
                          <Typography variant="body2" style={{ minWidth: 160 }}>
                            <strong>{fv.customFieldDefinition?.name}</strong>
                            <Typography component="span" variant="caption" color="textSecondary">
                              {" "}
                              ({fv.customFieldDefinition?.board?.name})
                            </Typography>
                          </Typography>
                          {editingForeignFieldId === fv.id ? (
                            <>
                              <TextField
                                size="small"
                                variant="outlined"
                                value={editingForeignFieldValue}
                                onChange={(e) => setEditingForeignFieldValue(e.target.value)}
                              />
                              <Button size="small" color="primary" onClick={() => handleSaveForeignFieldValue(fv.id)}>
                                Salvar
                              </Button>
                              <Button size="small" onClick={() => setEditingForeignFieldId(null)}>
                                Cancelar
                              </Button>
                            </>
                          ) : (
                            <>
                              <Typography variant="body2">{fv.value || "-"}</Typography>
                              {cardHistory.allowEditForeignData && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingForeignFieldId(fv.id);
                                    setEditingForeignFieldValue(fv.value || "");
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}
                            </>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </>
            )}
          </Box>
        ) : (
          <>
        <Box mb={2}>
          {(card.ticket?.contact?.number || card.contact?.number) ? (
            <Typography variant="body2" color="textSecondary">
              {card.ticket?.contact?.number || card.contact?.number}
            </Typography>
          ) : (
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <InputMask
                mask="(99) 99999-9999"
                maskChar={null}
                value={linkPhone}
                onChange={(e) => setLinkPhone(e.target.value)}
              >
                {(inputProps) => (
                  <TextField
                    {...inputProps}
                    size="small"
                    variant="outlined"
                    label="Vincular telefone (WhatsApp)"
                    style={{ minWidth: 200 }}
                  />
                )}
              </InputMask>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={linkingContact ? <CircularProgress size={14} /> : <LinkIcon />}
                onClick={handleLinkContact}
                disabled={linkingContact || !linkPhone.replace(/\D/g, "")}
              >
                Vincular
              </Button>
            </Box>
          )}
          <Typography variant="body2">{card.ticket?.lastMessage}</Typography>
        </Box>

        {/* Mesma funcionalidade que ja existia so' no verso do card
            (icone de calendario no Kanban, Boards/index.js MyCard) -
            pedido do usuario 2026-09-16 pra tambem aparecer aqui, ja que
            o card abre por esse modal em outros lugares alem do Kanban
            (ex: tela Comercial > Painel > tabela de prospects) onde o
            verso do card com o icone nunca aparece. */}
        <Box mb={2}>
          <Button
            size="small"
            variant="outlined"
            color={card.nextAppointment ? "primary" : "default"}
            startIcon={<EventIcon />}
            onClick={() => setAppointmentModalOpen(true)}
          >
            {card.nextAppointment
              ? `Compromisso: ${moment(card.nextAppointment.startAt).format("DD/MM HH:mm")}`
              : "Agendar compromisso"}
          </Button>
        </Box>

        {phases && phases.length > 0 && (
          <TextField
            select
            label="Fase"
            fullWidth
            margin="dense"
            variant="outlined"
            value={phaseId}
            onChange={(e) => setPhaseId(e.target.value)}
          >
            {phases.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* Botoes de regra "manual" aplicaveis a' fase ATUAL do card -
            pedido 2026-10-01: "preciso que o operador escolha se vai
            para tecnica ou implantacao" (duas regras manuais na mesma
            fase) e "voltar o board... precisa ter em todos" (regra
            manual com triggerValue vazio = qualquer fase). Configuradas
            em BoardSettingsModal > Automacao. */}
        {movementRules.filter(
          (r) =>
            r.triggerType === "manual" &&
            r.active !== false &&
            (!r.triggerValue || String(r.triggerValue) === String(card.phaseId))
        ).length > 0 && (
          <Box mb={2} display="flex" gap={1} flexWrap="wrap">
            {movementRules
              .filter(
                (r) =>
                  r.triggerType === "manual" &&
                  r.active !== false &&
                  (!r.triggerValue || String(r.triggerValue) === String(card.phaseId))
              )
              .map((rule) => (
                <Button
                  key={rule.id}
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={
                    applyingRuleId === rule.id ? <CircularProgress size={14} /> : <ArrowForward />
                  }
                  disabled={!!applyingRuleId}
                  onClick={() => handleApplyMovementRule(rule)}
                >
                  {rule.name || `Mover para ${rule.targetBoard?.name || "outro quadro"}`}
                </Button>
              ))}
          </Box>
        )}

        <TextField
          select
          label="Responsável"
          fullWidth
          margin="dense"
          variant="outlined"
          value={responsavelId}
          onChange={(e) => setResponsavelId(e.target.value)}
        >
          <MenuItem value="">
            <em>Sem responsável</em>
          </MenuItem>
          {users.map((u) => (
            <MenuItem key={u.id} value={u.id}>
              {u.name}
            </MenuItem>
          ))}
        </TextField>

        <Box mt={1}>
          <Typography variant="caption" color="textSecondary">
            Temperatura
          </Typography>
          <Box display="flex" gap={1} mt={0.5}>
            {/* Clique de novo na mesma pra desmarcar - pedido do usuario
                2026-08-28, tag clicavel em vez de lista suspensa. */}
            {TEMPERATURE_OPTIONS.map((opt) => {
              const selected = temperature === opt.value;
              return (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  size="small"
                  onClick={() => setTemperature(selected ? "" : opt.value)}
                  style={{
                    fontWeight: "bold",
                    color: selected ? "#fff" : opt.color,
                    backgroundColor: selected ? opt.color : opt.background,
                    cursor: "pointer",
                  }}
                />
              );
            })}
          </Box>
        </Box>

        <Box mt={1} mb={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">Resumo (mostrado no card)</Typography>
            <Button
              size="small"
              startIcon={generatingSummary ? <CircularProgress size={14} /> : <FlashOn />}
              onClick={handleGenerateSummary}
              disabled={generatingSummary}
            >
              Gerar com IA
            </Button>
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            margin="dense"
            variant="outlined"
            placeholder="Escreva um resumo curto ou gere com IA a partir dos campos preenchidos"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
          />
        </Box>

        {visibleFieldDefinitions.map((def) => (
          <CardFieldInput
            key={def.id}
            def={def}
            value={values[def.id]}
            onChange={(value) => handleChange(def.id, value)}
          />
        ))}

        <Box mt={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">Anexos</Typography>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={handleFileSelected}
            />
            <Button
              size="small"
              startIcon={<AttachFile />}
              onClick={handleUploadClick}
              disabled={uploading}
            >
              {uploading ? "Enviando..." : "Anexar arquivo"}
            </Button>
          </Box>
          {(card.attachments || []).length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              Nenhum anexo ainda.
            </Typography>
          ) : (
            <List dense>
              {(card.attachments || []).map((att) => {
                const fileUrl = `${getBackendUrl()}/public/company${card.companyId}/cardAttachments/${card.id}/${att.filename}`;
                const isImage = (att.mimetype || "").startsWith("image/");
                // Anexo que veio de OUTRO board (card foi movido depois
                // do upload) fica travado pra exclusao, a menos que
                // este board tenha "autorizar edicao" ligado - pedido
                // 2026-10-01 ("o historico precisa conter tudo inclusive
                // os anexos, porem nao deixar editar").
                const isForeign = att.originBoardId && att.originBoardId !== card.boardId;
                const locked = isForeign && !cardHistory?.allowEditForeignData;
                return (
                  <ListItem
                    key={att.id}
                    button
                    component="a"
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {isImage ? (
                      <img
                        src={fileUrl}
                        alt={att.originalName}
                        style={{ width: 32, height: 32, objectFit: "cover", borderRadius: 4, marginRight: 8 }}
                      />
                    ) : (
                      <Description fontSize="small" style={{ marginRight: 8 }} />
                    )}
                    <ListItemText
                      primary={att.originalName}
                      secondary={
                        isForeign ? (
                          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <LockIcon style={{ fontSize: 12 }} />
                            {`Veio de outro quadro${locked ? " - travado" : ""}`}
                          </span>
                        ) : undefined
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        component="a"
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Visualizar"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          handleDeleteAttachment(att.id);
                        }}
                        disabled={locked}
                        title={locked ? "Travado - veio de outro quadro" : "Excluir"}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>

        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Tarefas
          </Typography>

          {(card.tasks || []).length > 0 && (
            <List dense>
              {(card.tasks || []).map((task) => {
                const overdue =
                  !task.done && task.dueDate && moment(task.dueDate).isBefore(moment());
                return (
                  <ListItem
                    key={task.id}
                    disableGutters
                    button
                    onClick={() => setEditingTask(task)}
                  >
                    <ListItemIcon style={{ minWidth: 36 }} onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        edge="start"
                        checked={task.done}
                        onChange={() => handleToggleTask(task)}
                        size="small"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <span style={task.done ? { textDecoration: "line-through", color: "#9e9e9e" } : undefined}>
                          {task.title}
                        </span>
                      }
                      secondary={
                        <>
                          {task.assignedToAll ? "Todos" : task.assignedUser?.name || "Sem responsável"}
                          {task.createdByUser && ` · criada por ${task.createdByUser.name}`}
                          {task.dueDate && (
                            <>
                              {" · "}
                              <span style={overdue ? { color: "#c62828", fontWeight: "bold" } : undefined}>
                                {overdue ? "Atrasada: " : ""}
                                {moment(task.dueDate).format("DD/MM HH:mm")}
                              </span>
                            </>
                          )}
                        </>
                      }
                    />
                    <ListItemSecondaryAction onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" onClick={() => setEditingTask(task)} title="Editar tarefa">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteTask(task.id)} title="Excluir tarefa">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}

          <Box display="flex" gap={1} alignItems="flex-end" flexWrap="wrap" mt={1}>
            <TextField
              label="Nova tarefa"
              size="small"
              variant="outlined"
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              style={{ flex: "1 1 160px" }}
            />
            <TextField
              select
              label="Responsável"
              size="small"
              variant="outlined"
              value={newTaskAssignee}
              onChange={(e) => setNewTaskAssignee(e.target.value)}
              style={{ minWidth: 140 }}
            >
              <MenuItem value="">
                <em>Ninguém</em>
              </MenuItem>
              <MenuItem value="all">
                <em>Todos</em>
              </MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Prazo"
              type="datetime-local"
              size="small"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              value={newTaskDueDate}
              onChange={(e) => setNewTaskDueDate(e.target.value)}
            />
            <IconButton
              color="primary"
              onClick={handleAddTask}
              disabled={savingTask || !newTaskTitle.trim()}
              title="Adicionar tarefa"
            >
              <AddIcon />
            </IconButton>
          </Box>
        </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmDeleteOpen(true)} style={{ color: "#c62828", marginRight: "auto" }}>
          Excluir card
        </Button>
        {card.ticket && (
          <Button onClick={handleOpenTicket} color="default">
            Ver ticket
          </Button>
        )}
        <Button onClick={onClose} color="secondary">
          Cancelar
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          Salvar
        </Button>
      </DialogActions>

      <ConfirmationModal
        title="Excluir este card?"
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteCard}
      >
        {card.ticket
          ? "O ticket e a conversa continuam intactos - so o card sai do quadro."
          : "Essa acao nao pode ser desfeita."}
      </ConfirmationModal>

      <TaskEditModal
        open={!!editingTask}
        task={editingTask}
        onClose={() => setEditingTask(null)}
        onSaved={onSaved}
      />

      {appointmentModalOpen && (
        <AppointmentModal
          open={appointmentModalOpen}
          onClose={() => setAppointmentModalOpen(false)}
          appointment={card.nextAppointment}
          cardId={card.id}
          ticketId={card.ticket?.id}
          contactId={card.ticket?.contact?.id || card.contact?.id}
          reload={onSaved}
        />
      )}
    </Dialog>
  );
};

export default BoardCardModal;
